import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { useSite } from "../context/SiteContext";
import { postContact } from "../lib/api";
import { toast } from "../components/ui/sonner";
import { IconPin, IconGlobe, IconMail, IconPhone, IconClock } from "../components/Icons";

const empty = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const { settings } = useSite();
  const c = settings?.contact || {};
  const [params] = useSearchParams();
  const isConsultation = params.get("intent") === "consultation";
  const [form, setForm] = useState({ ...empty, subject: isConsultation ? "Request a Consultation" : "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setErrors((x) => ({ ...x, [k]: "" })); };

  const validate = () => {
    const err = {};
    if (form.name.trim().length < 2) err.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Please enter a valid email address.";
    if (form.subject.trim().length < 2) err.subject = "Please enter a subject.";
    if (form.message.trim().length < 5) err.message = "Please enter a short message.";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await postContact(form);
      toast.success("Thank you — your message has been received.", {
        description: "Our team will get back to you as soon as possible.",
      });
      setForm({ ...empty, subject: isConsultation ? "Request a Consultation" : "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const rows = [
    { icon: <IconPin />, k: "Location", v: c.location },
    { icon: <IconGlobe />, k: "Website", v: c.website },
    { icon: <IconMail />, k: "Email", v: c.email, href: `mailto:${c.email}` },
    { icon: <IconPhone />, k: "Phone", v: c.phone, href: `tel:${c.phone_href || ""}` },
    { icon: <IconClock />, k: "Working hours", v: c.hours },
  ];

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Get in touch"
        title={isConsultation ? "Request a Consultation" : "Contact us"}
        lead="Tell us about your quality and compliance needs. Proudly serving Somalia & the Horn of Africa."
        crumb="Contact"
      />
      <section className="section bg-paper">
        <div className="wrap contact-cols">
          <Reveal>
            <form onSubmit={submit} noValidate data-testid="contact-form">
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="name">Full name</label>
                  <input id="name" type="text" value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name" />
                  {errors.name && <span className="err" data-testid="error-name">{errors.name}</span>}
                </div>
                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input id="email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="contact-email" />
                  {errors.email && <span className="err" data-testid="error-email">{errors.email}</span>}
                </div>
                <div className="field">
                  <label htmlFor="phone">Phone <span style={{ color: "var(--muted)", fontWeight: 400 }}>(optional)</span></label>
                  <input id="phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+252 …" data-testid="contact-phone" />
                </div>
                <div className="field">
                  <label htmlFor="subject">Subject</label>
                  <input id="subject" type="text" value={form.subject} onChange={set("subject")} placeholder="How can we help?" data-testid="contact-subject" />
                  {errors.subject && <span className="err" data-testid="error-subject">{errors.subject}</span>}
                </div>
                <div className="field full">
                  <label htmlFor="message">Message</label>
                  <textarea id="message" value={form.message} onChange={set("message")} placeholder="Tell us about your needs…" data-testid="contact-message" />
                  {errors.message && <span className="err" data-testid="error-message">{errors.message}</span>}
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={submitting} style={{ marginTop: 20 }} data-testid="contact-submit">
                {submitting ? "Sending…" : "Send message"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={80}>
            <div className="info-card">
              <span className="eyebrow">Contact details</span>
              <h2 style={{ fontSize: "1.4rem", margin: "14px 0 8px" }}>Talk to our team</h2>
              {rows.map((r) => (
                <div className="info-row" key={r.k}>
                  <div className="ico">{r.icon}</div>
                  <div>
                    <div className="k">{r.k}</div>
                    {r.href ? (
                      <a className="v" href={r.href} data-testid={`contact-info-${r.k.toLowerCase().replace(/\s/g, "-")}`}>{r.v}</a>
                    ) : (
                      <div className="v" data-testid={`contact-info-${r.k.toLowerCase().replace(/\s/g, "-")}`}>{r.v}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
